"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  AlertCircle,
  Database,
  Eye,
  FileText,
  Lock,
  Mail,
  Shield,
  UserCheck,
} from "lucide-react";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 py-12 px-4">
      <div className="container max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-4 py-2 rounded-full mb-4">
            <Shield className="h-5 w-5" />
            <span className="font-semibold">Política de Privacidade</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Agendei - Política de Privacidade
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            Última atualização: {new Date().toLocaleDateString("pt-BR")}
          </p>
        </div>

        {/* Introduction */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Eye className="h-5 w-5 text-blue-600" />
              <CardTitle>1. Introdução</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              A <strong>Agendei</strong> (&quot;<strong>nós</strong>&quot;,
              &quot;
              <strong>nosso</strong>&quot; ou &quot;<strong>nossa</strong>
              &quot;) valoriza e respeita a privacidade de todos os usuários de
              nossa plataforma. Esta Política de Privacidade descreve como
              coletamos, usamos, armazenamos e protegemos os dados pessoais de
              nossos usuários, em conformidade com a Lei Geral de Proteção de
              Dados Pessoais (Lei nº 13.709/2018 - <strong>LGPD</strong>).
            </p>
            <p>
              Nossa plataforma oferece serviços de agendamento online para
              estabelecimentos comerciais (<strong>&quot;Tenants&quot;</strong>)
              e seus clientes (<strong>&quot;Usuários Finais&quot;</strong>). Ao
              utilizar nossos serviços, você concorda com a coleta e uso de
              informações de acordo com esta política.
            </p>
          </CardContent>
        </Card>

        {/* Data Collection */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Database className="h-5 w-5 text-blue-600" />
              <CardTitle>2. Dados Coletados</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 text-gray-700 dark:text-gray-300">
            <div>
              <h3 className="font-semibold text-lg mb-3">
                2.1. Dados de Estabelecimentos (Tenants)
              </h3>
              <p className="mb-2">
                Quando você se registra como estabelecimento, coletamos:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>
                  <strong>Dados de Cadastro:</strong> Nome do estabelecimento,
                  slug, email, telefone, endereço
                </li>
                <li>
                  <strong>Dados de Autenticação:</strong> Email e senha
                  (criptografada)
                </li>
                <li>
                  <strong>Dados de Personalização:</strong> Logo, banner,
                  descrição do negócio
                </li>
                <li>
                  <strong>Dados de Serviços:</strong> Informações sobre serviços
                  oferecidos, preços e duração
                </li>
                <li>
                  <strong>Dados de Funcionamento:</strong> Horários de
                  funcionamento e fechamentos
                </li>
                <li>
                  <strong>Dados de Assinatura:</strong> Status de assinatura,
                  datas de vencimento
                </li>
                <li>
                  <strong>Dados de Pagamento:</strong> Informações de pagamentos
                  manuais registrados (sem dados bancários sensíveis)
                </li>
                <li>
                  <strong>Dados de Referral:</strong> Códigos de indicação e
                  comissões
                </li>
              </ul>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold text-lg mb-3">
                2.2. Dados de Clientes (Usuários Finais)
              </h3>
              <p className="mb-2">Quando você agenda um serviço, coletamos:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>
                  <strong>Dados Pessoais:</strong> Nome completo, telefone,
                  email (opcional)
                </li>
                <li>
                  <strong>Dados de Agendamento:</strong> Data, horário, serviço
                  selecionado, observações
                </li>
                <li>
                  <strong>Preferências de Comunicação:</strong> Opt-in para
                  receber comunicações (opcional)
                </li>
                <li>
                  <strong>Avaliações:</strong> Nota, comentário e resposta do
                  estabelecimento (quando aplicável)
                </li>
              </ul>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold text-lg mb-3">
                2.3. Dados de Administradores da Plataforma
              </h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>
                  <strong>Dados de Autenticação:</strong> Email e senha
                  (criptografada)
                </li>
                <li>
                  <strong>Dados de Perfil:</strong> Nome e status de atividade
                </li>
              </ul>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold text-lg mb-3">
                2.4. Dados Coletados Automaticamente
              </h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>
                  <strong>Dados de Navegação:</strong> Endereço IP, tipo de
                  navegador, páginas visitadas
                </li>
                <li>
                  <strong>Cookies:</strong> Utilizamos cookies essenciais para
                  funcionamento da plataforma
                </li>
                <li>
                  <strong>Logs de Sistema:</strong> Registros de acesso e
                  atividades para segurança
                </li>
                <li>
                  <strong>Dados de Erro:</strong> Informações técnicas de erros
                  via Sentry
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Data Usage */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-5 w-5 text-blue-600" />
              <CardTitle>3. Uso dos Dados</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p className="font-semibold">
              Utilizamos seus dados pessoais para:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Prestação de Serviços:</strong> Processar e gerenciar
                agendamentos, fornecer acesso à plataforma
              </li>
              <li>
                <strong>Comunicação:</strong> Enviar confirmações de
                agendamento, lembretes, atualizações importantes
              </li>
              <li>
                <strong>Melhoria da Plataforma:</strong> Analisar uso e melhorar
                funcionalidades
              </li>
              <li>
                <strong>Segurança:</strong> Detectar e prevenir fraudes, abusos
                e violações de segurança
              </li>
              <li>
                <strong>Cobrança:</strong> Gerenciar assinaturas, pagamentos e
                faturamento
              </li>
              <li>
                <strong>Sistema de Indicação:</strong> Processar indicações e
                comissões entre estabelecimentos
              </li>
              <li>
                <strong>Sistema de Avaliações:</strong> Permitir que clientes
                avaliem serviços e estabelecimentos respondam
              </li>
              <li>
                <strong>Suporte:</strong> Responder solicitações e resolver
                problemas
              </li>
              <li>
                <strong>Cumprimento Legal:</strong> Atender obrigações legais e
                regulatórias
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Legal Basis */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <UserCheck className="h-5 w-5 text-blue-600" />
              <CardTitle>4. Base Legal para Tratamento de Dados</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              O tratamento de dados pessoais na Agendei está fundamentado nas
              seguintes bases legais da LGPD:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Execução de Contrato (Art. 7º, V):</strong> Para
                prestação dos serviços de agendamento
              </li>
              <li>
                <strong>Consentimento (Art. 7º, I):</strong> Para opt-in de
                comunicações e avaliações públicas
              </li>
              <li>
                <strong>Legítimo Interesse (Art. 7º, IX):</strong> Para melhoria
                da plataforma, segurança e prevenção de fraudes
              </li>
              <li>
                <strong>Cumprimento de Obrigação Legal (Art. 7º, II):</strong>{" "}
                Para atendimento de requisitos legais e fiscais
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Data Sharing */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              <CardTitle>5. Compartilhamento de Dados</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p className="font-semibold">
              Seus dados podem ser compartilhados com:
            </p>

            <div>
              <h4 className="font-semibold mb-2">
                5.1. Estabelecimentos (Tenants)
              </h4>
              <p>
                Dados de clientes são compartilhados com o estabelecimento onde
                o agendamento foi realizado.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">
                5.2. Provedores de Serviços
              </h4>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>
                  <strong>Hospedagem:</strong> Vercel (servidor web e aplicação)
                </li>
                <li>
                  <strong>Banco de Dados:</strong> PostgreSQL (armazenamento de
                  dados)
                </li>
                <li>
                  <strong>Armazenamento de Arquivos:</strong> MinIO (logos,
                  banners, imagens)
                </li>
                <li>
                  <strong>Monitoramento de Erros:</strong> Sentry (detecção e
                  correção de problemas)
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">5.3. Autoridades Legais</h4>
              <p>
                Quando exigido por lei, ordem judicial ou autoridades
                competentes.
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg">
              <p className="font-semibold text-blue-900 dark:text-blue-200">
                ⚠️ Importante: Não vendemos, alugamos ou compartilhamos seus
                dados pessoais com terceiros para fins de marketing.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Data Security */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Lock className="h-5 w-5 text-blue-600" />
              <CardTitle>6. Segurança dos Dados</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              Implementamos medidas técnicas e organizacionais para proteger
              seus dados:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Criptografia:</strong> Senhas são armazenadas com hash
                seguro (bcrypt)
              </li>
              <li>
                <strong>HTTPS:</strong> Todas as comunicações são criptografadas
                via SSL/TLS
              </li>
              <li>
                <strong>Controle de Acesso:</strong> Sistema de autenticação e
                autorização por níveis
              </li>
              <li>
                <strong>Backup:</strong> Backups regulares do banco de dados
              </li>
              <li>
                <strong>Monitoramento:</strong> Logs de auditoria e detecção de
                anomalias
              </li>
              <li>
                <strong>Anonimização:</strong> Nomes de clientes são
                anonimizados em avaliações públicas
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Data Retention */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>7. Retenção de Dados</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              Mantemos seus dados pelo tempo necessário para cumprir as
              finalidades descritas nesta política:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Dados de Agendamentos:</strong> Mantidos enquanto a
                conta do estabelecimento estiver ativa e por 5 anos após
                encerramento (obrigações legais e fiscais)
              </li>
              <li>
                <strong>Dados de Clientes:</strong> Mantidos enquanto houver
                relação com o estabelecimento
              </li>
              <li>
                <strong>Dados de Avaliações:</strong> Mantidos indefinidamente,
                exceto mediante solicitação de exclusão
              </li>
              <li>
                <strong>Logs de Sistema:</strong> Mantidos por 6 meses para fins
                de segurança
              </li>
            </ul>
            <p className="mt-4">
              Após o período de retenção, os dados são excluídos ou anonimizados
              de forma segura.
            </p>
          </CardContent>
        </Card>

        {/* User Rights */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-5 w-5 text-blue-600" />
              <CardTitle>8. Seus Direitos (LGPD)</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>Conforme a LGPD, você tem os seguintes direitos:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Confirmação e Acesso:</strong> Confirmar se tratamos
                seus dados e acessá-los
              </li>
              <li>
                <strong>Correção:</strong> Corrigir dados incompletos, inexatos
                ou desatualizados
              </li>
              <li>
                <strong>Anonimização, Bloqueio ou Eliminação:</strong> Solicitar
                anonimização, bloqueio ou eliminação de dados desnecessários ou
                excessivos
              </li>
              <li>
                <strong>Portabilidade:</strong> Solicitar portabilidade dos
                dados a outro fornecedor
              </li>
              <li>
                <strong>Eliminação:</strong> Solicitar eliminação dos dados
                tratados com consentimento
              </li>
              <li>
                <strong>Informação sobre Compartilhamento:</strong> Saber com
                quem compartilhamos seus dados
              </li>
              <li>
                <strong>Informação sobre Consentimento:</strong> Ser informado
                sobre a possibilidade de não fornecer consentimento
              </li>
              <li>
                <strong>Revogação do Consentimento:</strong> Revogar
                consentimento a qualquer momento
              </li>
            </ul>

            <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg mt-4">
              <p className="font-semibold text-green-900 dark:text-green-200 mb-2">
                📧 Para exercer seus direitos, entre em contato:
              </p>
              <p className="text-green-800 dark:text-green-300">
                Email:{" "}
                <a
                  href="mailto:privacidade@agendei.kayooliveira.com"
                  className="underline"
                >
                  privacidade@agendei.kayooliveira.com
                </a>
              </p>
              <p className="text-sm text-green-700 dark:text-green-400 mt-2">
                Responderemos sua solicitação em até 15 dias úteis.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Cookies */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>9. Cookies e Tecnologias Similares</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              Utilizamos cookies essenciais para o funcionamento da plataforma:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Cookies de Autenticação:</strong> Manter sua sessão
                ativa após login
              </li>
              <li>
                <strong>Cookies de Preferências:</strong> Lembrar suas
                configurações (tema escuro/claro)
              </li>
              <li>
                <strong>Cookies de Segurança:</strong> Proteger contra fraudes e
                ataques
              </li>
            </ul>
            <p className="mt-4">
              Você pode configurar seu navegador para recusar cookies, mas isso
              pode limitar algumas funcionalidades da plataforma.
            </p>
          </CardContent>
        </Card>

        {/* Children */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>10. Crianças e Adolescentes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              Nossa plataforma não é direcionada a menores de 18 anos.
              Agendamentos para menores devem ser realizados por seus
              responsáveis legais. Não coletamos intencionalmente dados de
              crianças e adolescentes sem o consentimento dos pais ou
              responsáveis.
            </p>
          </CardContent>
        </Card>

        {/* International Transfers */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>11. Transferência Internacional de Dados</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              Alguns de nossos provedores de serviços podem estar localizados
              fora do Brasil. Nesses casos, garantimos que medidas adequadas de
              proteção sejam implementadas conforme exigido pela LGPD, incluindo
              cláusulas contratuais padrão e garantias de segurança.
            </p>
          </CardContent>
        </Card>

        {/* Changes */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>12. Alterações nesta Política</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              Podemos atualizar esta Política de Privacidade periodicamente.
              Notificaremos sobre alterações significativas através de aviso em
              nossa plataforma ou por email. A data da última atualização sempre
              estará indicada no início deste documento.
            </p>
            <p>
              Recomendamos que você revise esta política regularmente para se
              manter informado sobre como protegemos seus dados.
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="mb-12">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-5 w-5 text-blue-600" />
              <CardTitle>13. Contato e Encarregado de Dados (DPO)</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              Para questões sobre esta Política de Privacidade, exercício de
              direitos ou outras dúvidas relacionadas à proteção de dados, entre
              em contato com nosso Encarregado de Proteção de Dados:
            </p>
            <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-lg space-y-2">
              <p>
                <strong>Email:</strong>{" "}
                <a
                  href="mailto:privacidade@agendei.kayooliveira.com"
                  className="text-blue-600 dark:text-blue-400 underline"
                >
                  privacidade@agendei.kayooliveira.com
                </a>
              </p>
              <p>
                <strong>Prazo de Resposta:</strong> Até 15 dias úteis
              </p>
            </div>
            <p className="mt-4">
              Também você pode registrar reclamações junto à Autoridade Nacional
              de Proteção de Dados (ANPD) através do site:{" "}
              <a
                href="https://www.gov.br/anpd"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 underline"
              >
                www.gov.br/anpd
              </a>
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-600 dark:text-gray-400 pb-8">
          <p>
            © {new Date().getFullYear()} Agendei. Todos os direitos reservados.
          </p>
          <p className="mt-2">
            Documento em conformidade com a Lei nº 13.709/2018 (LGPD)
          </p>
        </div>
      </div>
    </div>
  );
}
